import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Send,
  Lock,
  Unlock,
  Trash2,
  MessageCircle,
} from "lucide-react";
import { useThreadMessages } from "@/hooks/useThreads";
import { useThreads } from "@/hooks/useThreads";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useDisplayNames } from "@/hooks/useUserProfile";

interface ThreadDetailProps {
  threadId: string;
  onBack: () => void;
}

export default function ThreadDetail({ threadId, onBack }: ThreadDetailProps) {
  const { thread, messages, loading, sendMessage } =
    useThreadMessages(threadId);
  const { closeThread, reopenThread, deleteThread } = useThreads();
  const { user } = useAuth();
  const { toast } = useToast();
  const { getDisplayName, getInitial } = useDisplayNames();
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isOwner = thread?.user_id === user?.id;
  const isClosed = thread?.status === "closed";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);

    const { error } = await sendMessage(newMessage.trim());
    if (error) {
      toast({
        title: "Blad",
        description: error,
        variant: "destructive",
      });
    } else {
      setNewMessage("");
    }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = async () => {
    const { error } = await closeThread(threadId);
    if (error) {
      toast({
        title: "Blad",
        description: error,
        variant: "destructive",
      });
    } else {
      toast({ title: "Watek zamkniety" });
    }
  };

  const handleReopen = async () => {
    const { error } = await reopenThread(threadId);
    if (error) {
      toast({
        title: "Blad",
        description: error,
        variant: "destructive",
      });
    } else {
      toast({ title: "Watek otwarty ponownie" });
    }
  };

  const handleDelete = async () => {
    const { error } = await deleteThread(threadId);
    if (error) {
      toast({
        title: "Blad",
        description: error,
        variant: "destructive",
      });
    } else {
      toast({ title: "Watek usuniety" });
      onBack();
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-1/3 mb-4" />
          <div className="h-20 bg-muted rounded mb-4" />
          <div className="h-16 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Watek nie znaleziony</p>
        <Button variant="ghost" onClick={onBack} className="mt-2">
          Wroc do listy
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Naglowek watku */}
      <div className="flex items-center justify-between gap-2 pb-4 border-b">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-lg truncate">{thread.title}</h2>
              <Badge variant={isClosed ? "secondary" : "default"}>
                {isClosed ? "Zamkniety" : "Otwarty"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Utworzono: {formatDate(thread.created_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {isClosed ? (
            <Button variant="outline" size="sm" onClick={handleReopen}>
              <Unlock className="h-4 w-4 mr-1" />
              Otworz
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleClose}>
              <Lock className="h-4 w-4 mr-1" />
              Zamknij
            </Button>
          )}

          {isOwner && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Usunac watek?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Ta operacja jest nieodwracalna. Wszystkie wiadomosci w watku
                    zostana usuniete.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Anuluj</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Usun
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Tresc watku (pierwszy post) */}
      {thread.content && (
        <Card className="mt-4 border-l-4 border-l-primary">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                {getInitial(thread.user_id)}
              </div>
              <div>
                <span className="text-sm font-medium">
                  {getDisplayName(thread.user_id)}
                </span>
                <span className="text-xs text-muted-foreground ml-2">
                  {formatDate(thread.created_at)}
                </span>
              </div>
            </div>
            <p className="text-sm whitespace-pre-wrap pl-9">{thread.content}</p>
          </CardContent>
        </Card>
      )}

      {/* Lista wiadomosci */}
      <div className="flex-1 overflow-auto mt-4 space-y-3 min-h-0">
        {messages.length === 0 && !thread.content ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Brak wiadomosci. Napisz pierwsza!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.user_id === user?.id;
            return (
              <div
                key={msg.id}
                className={`flex gap-3 ${isMe ? "" : ""}`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                    isMe
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {getInitial(msg.user_id)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium">
                      {getDisplayName(msg.user_id)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(msg.created_at)}
                    </span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap bg-muted/50 rounded-lg p-3">
                    {msg.content}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Pole do pisania */}
      {!isClosed ? (
        <div className="mt-4 pt-4 border-t">
          <div className="flex gap-2">
            <Textarea
              placeholder="Napisz wiadomosc... (Ctrl+Enter aby wyslac)"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[80px] resize-none"
            />
            <Button
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="shrink-0 self-end"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 pt-4 border-t text-center text-sm text-muted-foreground">
          <Lock className="h-4 w-4 inline mr-1" />
          Watek zamkniety - nie mozna dodawac wiadomosci
        </div>
      )}
    </div>
  );
}
